import React, { useState } from "react";
import { Stage1ConfirmQuestions } from "./Stage1ConfirmQuestions";
import { SkipComplianceModal } from "./SkipComplianceModal";

/** The two stages of Step 0. Stage 2 (the grid) is delivered by Task 009. */
export type PreComplianceStage = 1 | 2;

/**
 * Task 009 plugs the Stage 2 compliance grid in here. `PreComplianceStep`
 * renders `<Stage2ComplianceGrid rfxId={rfxId} onBack={...} onComplete={...} />`
 * via this prop once stage === 2; until then a placeholder is shown.
 *
 * Hand-off contract (Stage 1 → Stage 2):
 *  - Stage 1 saves the RFx-scoped questions (PUT) before calling `onProceed`,
 *    so by the time Stage 2 mounts the `["compliance", rfxId, "questions"]`
 *    cache is fresh and the grid can read the saved set (or its own GET grid).
 *  - `onComplete` should advance the stepper past Step 0 (same effect as skip).
 *  - `onBack` returns to Stage 1.
 */
export interface Stage2GridProps {
	rfxId: number;
	onBack: () => void;
	onComplete: () => void;
}

export interface PreComplianceStepProps {
	rfxId: number;
	/** Advance the parent stepper past Step 0 (skip-confirmed or grid-complete). */
	onStepComplete: () => void;
	/**
	 * Extension point for Task 009. When provided it is rendered for stage 2;
	 * otherwise a placeholder is shown. Wired so Task 009 only needs to supply
	 * its grid component without touching this container's stage logic.
	 */
	renderStage2?: (props: Stage2GridProps) => React.ReactNode;
}

export const PreComplianceStep = ({
	rfxId,
	onStepComplete,
	renderStage2,
}: PreComplianceStepProps) => {
	const [stage, setStage] = useState<PreComplianceStage>(1);
	const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);

	const handleProceed = () => {
		setStage(2);
	};

	const handleSkipConfirmed = () => {
		setIsSkipModalOpen(false);
		onStepComplete();
	};

	return (
		<div data-testid="pre-compliance-step">
			{stage === 1 && (
				<Stage1ConfirmQuestions
					rfxId={rfxId}
					onProceed={handleProceed}
					onSkip={() => setIsSkipModalOpen(true)}
				/>
			)}

			{stage === 2 &&
				(renderStage2 ? (
					renderStage2({
						rfxId,
						onBack: () => setStage(1),
						onComplete: onStepComplete,
					})
				) : (
					<div
						className="rounded-md border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500"
						data-testid="pre-compliance-stage-2-placeholder"
					>
						Compliance grid coming soon.
					</div>
				))}

			<SkipComplianceModal
				rfxId={rfxId}
				isOpen={isSkipModalOpen}
				onClose={() => setIsSkipModalOpen(false)}
				onConfirmed={handleSkipConfirmed}
			/>
		</div>
	);
};
